import { DateTime } from 'luxon';

import type { RecordOf } from 'immutable';

import type { QcsError } from '../core/resourceStoreTypes';
import PaymentMethodType from '../fmcore-types/PaymentMethodTypes';
import type { TagProps } from '../tags/tagsTypes';
import * as types from './transactionsTypes';

export const { mkSplit, mkSplitItem, mkSplitItems } = types;

// /////////////////////////////////////////////////////////////////////////////
// Transformers for Transaction API Responses to Transactions
// /////////////////////////////////////////////////////////////////////////////

// This function is used to merge the ids from a transaction response into the
// transaction resource sent. This will only work correctly if the requested
// resource is an Immutable Record (at least the top-level transaction and any
// split record and items.
//
// Note: When transaction API changes to return the entire resource, we can just
// replace instead of merging.
export const transformResourceWithResponse = (
  resource: types.TransactionProps | types.TransactionRecord,
  response: {
    data?: types.TransactionProps & {
      resource?: types.TransactionProps;
      correlationIds?: { [key: string]: string };
    };
  },
) => {
  // If the response does not have an id or the resource isn't an Immutable Record
  if (!response.data?.id || !('merge' in resource)) {
    return resource;
  }
  const updatedResource = resource.merge({
    id: response.data.id,
    split:
      resource.split && 'merge' in resource.split
        ? resource.split?.merge({
            items: resource.split?.items?.map((splitItem) => {
              const splitItemId =
                splitItem.id || (splitItem.clientId ? response.data?.correlationIds?.[splitItem.clientId] : null);
              const idMatch = splitItem.id === splitItemId;
              if (idMatch) {
                return 'merge' in splitItem ? splitItem : mkSplitItem(splitItem);
              }
              return 'merge' in splitItem
                ? splitItem.merge({ id: splitItemId })
                : mkSplitItem({ ...splitItem, id: splitItemId });
            }),
          })
        : null,
  });
  return updatedResource;
};

// This function is used to merge the ids from a batch transaction response into the
// transaction resources sent. This will only work correctly if the request
// resources are an Immutable Record (at least the top-level transaction and any
// split record and items.
//
// Note: When transaction API changes to return entire resources, we can just
// replace instead of merging.
export const transformResourcesWithResponse = (
  resources: Array<RecordOf<types.TransactionProps> | types.TransactionProps>,
  response: {
    data?: {
      items?: {
        resource?: types.TransactionProps & {
          status?: string;
          correlationIds?: { [key: string]: string };
        };
        errors?: QcsError[];
      }[];
    };
  },
) => {
  const data = response?.data;

  const errors: string[] = [];
  const newResources = data?.items?.map((item) => {
    let resource;
    if (item.resource?.status === 'ERROR' && item.errors) {
      errors.push(...item.errors.map((e) => JSON.stringify(e)));
    }
    const resourceItem = item.resource;
    const clientId = resourceItem?.clientId;
    const id = resourceItem?.id;
    if (clientId) {
      resource = resources.find((fResource) => fResource.clientId === clientId);
      if (resource && 'merge' in resource) {
        return resource.merge({
          id: resourceItem.id ?? resource.id,
          split:
            resource.split && 'merge' in resource.split
              ? resource.split?.merge({
                  items: resource.split?.items?.map((splitItem) => {
                    const splitItemId =
                      splitItem.id || (splitItem.clientId ? resourceItem.correlationIds?.[splitItem.clientId] : null);
                    const idMatch = splitItem.id === splitItemId;
                    if (idMatch) {
                      return 'merge' in splitItem ? splitItem : mkSplitItem(splitItem);
                    }
                    return 'merge' in splitItem
                      ? splitItem.merge({ id: splitItemId })
                      : mkSplitItem({ ...splitItem, id: splitItemId });
                  }),
                })
              : null,
        });
      } else {
        // eslint-disable-next-line no-console
        console.error('transformResourcesWithResponse: immutable resource not found');

        // maintain backwards compatibility with method from resourceStoreTypes when dealing with non-Immutable Records
        resource = resource != null ? { ...resource, id } : undefined;
      }
    } else {
      resource = resources.find((fResource) => fResource.id === id);
    }
    return resource;
  });

  if (errors.length > 0) {
    throw errors;
  }

  return newResources;
};

export function transformResponseToTransactions(response?: { data?: { resources?: types.TransactionProps[] } } | null) {
  const resources = response?.data?.resources;

  return resources ? transformQcsTransactionsToTransactions(resources) : resources;
}

export const transformQcsTransactionsToTransactions = (qcsTransactions: types.TransactionProps[]) =>
  qcsTransactions.reduce((txns, txn) => {
    const transformed = transformQcsTransactionToTransaction(txn);
    if (transformed) {
      txns.push(transformed);
    }
    return txns;
  }, [] as types.TransactionRecord[]);

export function transformQcsTransactionToTransaction(transaction?: types.TransactionProps | null) {
  if (!transaction) {
    assert(false, 'unexpected null or undefined transaction');
    return null;
  }
  switch (transaction.type) {
    case 'CASH_FLOW':
      return transformApiDataToCashFlowTxn(transaction);
    case 'INVESTMENT':
      return transformApiDataToInvestmentTxn(transaction);
    case 'INVOICE':
      return transformApiDataToInvoiceTxn(transaction);
    case 'PAYMENT':
      return transformApiDataToPaymentTxn(transaction);
    default:
      assert(false, `unexpected transaction type ${transaction.type}`);
      return null;
  }
}

export function transformApiDataToCashFlowTxn(data: types.TransactionProps) {
  return types.mkCashFlowTransaction(data);
}

export function transformApiDataToInvestmentTxn(qcsTransaction: types.TransactionProps) {
  return types.mkInvestmentTransaction(qcsTransaction);
}

export function transformApiDataToInvoiceTxn(qcsTransaction: types.TransactionProps) {
  return types.mkInvoiceTransaction(qcsTransaction);
}

export function transformApiDataToPaymentTxn(qcsTransaction: types.TransactionProps) {
  return types.mkPaymentTransaction(qcsTransaction);
}

// /////////////////////////////////////////////////////////////////////////////
// Transformers for Transactions to Transaction API Request Data
// /////////////////////////////////////////////////////////////////////////////

export function transformTransactionToRequestData(transaction: types.TransactionProps) {
  switch (transaction.type) {
    case 'CASH_FLOW':
      return transformCashFlowTxnToRequestData(transaction);
    case 'INVESTMENT':
      return transformInvestmentTxnToRequestData(transaction);
    case 'INVOICE':
      return transformInvoiceTxnToRequestData(transaction);
    case 'PAYMENT':
      return transformPaymentTxnToRequestData(transaction);
    default:
      assert(false, `unexpected transaction type ${transaction?.type}`);
      return transaction;
  }
}

export function transformTransactionsToRequestData(resources: Array<types.TransactionRecord | types.TransactionProps>) {
  return { resources: resources.map((txn) => transformTransactionToRequestData(txn)) };
}

function transformCashFlowTxnToRequestData(transaction: types.TransactionProps) {
  return {
    type: 'CASH_FLOW',
    ...transformBaseTransactionToRequestData(transaction),
    check: transaction.check,
    // coordinates: transaction.coordinates,
  } as const;
}

function transformInvestmentTxnToRequestData(transaction: types.TransactionProps) {
  return {
    type: 'INVESTMENT',
    ...transformBaseTransactionToRequestData(transaction),
    investmentTxnType: transaction.investmentTxnType,
    symbol: transaction.symbol,
    units: transaction.units,
    costBasis: transaction.costBasis,
    commission: transaction.commission,
    splitNumerator: transaction.splitNumerator,
    splitDenominator: transaction.splitDenominator,

    // props below never tested
    securityName: transaction.securityName,
    securityId: transaction.securityId,
    securityIdType: transaction.securityIdType,
    securityType: transaction.securityType,
    localSecurityId: transaction.localSecurityId,
  } as const;
}

function transformInvoiceTxnToRequestData(transaction: types.TransactionProps) {
  return {
    type: 'INVOICE',
    ...transformBaseTransactionToRequestData(transaction),

    allocations: transaction.allocations || null,

    invoiceNo: transaction.invoiceNo,
    includeBilledItemAttachments: transaction.includeBilledItemAttachments,
    includeInvoiceAttachments: transaction.includeInvoiceAttachments,

    toEmailAddresses: transaction.toEmailAddresses,
    ccEmailAddresses: transaction.ccEmailAddresses,
    bccEmailAddresses: transaction.bccEmailAddresses,

    sentOn: transaction.sentOn,

    billingAddress: transaction.billingAddress,
    termsId: transaction.termsId,
    dueOn: transaction.dueOn,

    locOfSale: transaction.locOfSale,

    shippingAddress: transaction.shippingAddress,
    shippingVia: transaction.shippingVia,
    shippingOn: transaction.shippingOn,
    shippingTrackingNo: transaction.shippingTrackingNo,

    msgOnInvoice: transaction.msgOnInvoice,
    msgOnStatement: transaction.msgOnStatement,

    emailSubject: transaction.emailSubject,
    emailMessage: transaction.emailMessage,

    paymentMethods:
      transaction.paymentMethods && 'toJS' in transaction.paymentMethods
        ? (transaction.paymentMethods?.toJS?.() as Array<PaymentMethodType>)
        : transaction.paymentMethods,
  } as const;
}

function transformPaymentTxnToRequestData(transaction: types.TransactionProps) {
  return {
    type: 'PAYMENT',
    ...transformBaseTransactionToRequestData(transaction),

    paymentTxnType: null,
  } as const;
}

function transformBaseTransactionToRequestData(transaction: types.TransactionProps) {
  return {
    id: transaction.id === '0' ? undefined : transaction?.id,

    accountId: transaction.accountId,

    businessId: transaction.businessId,
    customerId: transaction.customerId,
    projectId: transaction.projectId,

    clientId: transaction.clientId,
    postedOn: transaction.postedOn,

    payee: transaction.payee,
    memo: transaction.memo,
    knownCategoryId: transaction.knownCategoryId,
    coa: transaction.coa
      ? {
          type: transaction.coa.type,
          id: transaction.coa.id,
        }
      : undefined,
    amount: transaction.amount,

    tags: (transaction.tags && 'toJS' in transaction.tags
      ? transaction.tags?.map((tag) => ({ id: tag.id })).toJS?.()
      : undefined) as TagProps[],
    attachments: transaction.attachments || undefined,

    split:
      transaction.split && 'toJS' in transaction.split
        ? (transaction.split?.toJS?.() as types.SplitProps)
        : transaction.split,
    state: transaction.state,
    matchState: transaction.matchState,
    isReviewed: transaction.isReviewed,

    cpData: transaction.cpData || undefined,

    source: transaction.source,
    stModelId: transaction.stModelId || undefined,
    stDueOn: transaction.stDueOn || undefined,
    paymentProvider: transaction.paymentProvider || undefined,

    isExcludedFromF2S: transaction.isExcludedFromF2S,
    isExcludedFromReports: transaction.isExcludedFromReports,

    userFlag: transaction.userFlag || undefined,
    userFlagNote: transaction.userFlagNote || undefined,
    userModifiedAt: DateTime.local().toISO(), // so QCS knows how to resolve race-condition conflicts

    isDeleted: transaction.isDeleted,
    transfer: transaction.transfer || undefined,

    isBill: transaction.isBill,
    isSubscription: transaction.isSubscription,

    isBillable: transaction.isBillable,

    subtype: transaction.subtype,
    subtypeRelatedTxn: transaction.subtypeRelatedTxn,
    linkedItem: transaction.linkedItem,

    allocations:
      transaction.allocations && 'toJS' in transaction.allocations
        ? (transaction.allocations?.toJS?.() as types.AllocationProps[])
        : transaction.allocations,
  };
}
